import type { ApiLogsListParams, ApiLogsSortKey } from '../types/pages'
/**
 * API Logs — React parity with Angular `ReportsApiLogsComponent`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronsUpDown, Eye, Loader2 } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ROUTE_BUILDERS } from '@/config/routes'
import { COMPANY_ROLES } from '@/constants/roles'
import { useUserRole } from '@/hooks/useUserRole'
import { showApiError } from '@/lib/utils/notifications'
import { cn } from '@lib/utils/helpers'
import { ReportTableScroll } from '../components/ReportTableScroll'
import reportStyles from '../styles/reportsTable.module.css'
import {
  ApiLogsFilters,
  type ApiLogsFilterState,
} from '../components/ApiLogsFilters'
import { countActiveApiLogsFilters } from '../utils/apiLogsFilterCount'
import { getApiLogList } from '../services/reportsService'
import type { ApiLogRow } from '../types/api'
import { ymdToDmy } from '../utils/reportDateParams'
import {
  buildApiLogsSessionFromParams,
  clearApiLogsSession,
  readApiLogsSession,
  sessionFilterToApiLogsPatch,
  writeApiLogsSession,
} from '../utils/apiLogsSessionFilter'
import {
  API_LOGS_SORT_COLUMNS as SORT_COLUMNS,
  DEFAULT_API_LOGS_PARAMS as DEFAULT_PARAMS,
} from '../constants/pages/apiLogsPage'

function initialParams(): ApiLogsListParams {
  const session = readApiLogsSession()
  if (session) {
    return { ...DEFAULT_PARAMS, ...sessionFilterToApiLogsPatch(session) }
  }
  return { ...DEFAULT_PARAMS }
}

function buildApiLogsQueryParams(p: ApiLogsListParams): URLSearchParams {
  const qs = new URLSearchParams()
  qs.set('page', String(p.page))
  qs.set('perPage', String(p.perPage))
  qs.set('sortBy', p.sortBy)
  qs.set('orderBy', p.orderBy)
  if (p.q.trim()) qs.set('q', p.q.trim())
  if (p.property_id) qs.set('property_id', p.property_id)
  const fromApi = ymdToDmy(p.fromDt)
  const toApi = ymdToDmy(p.toDt)
  if (fromApi) qs.set('fromDt', fromApi)
  if (toApi) qs.set('toDt', toApi)
  return qs
}

function truncateBody(value: string | undefined, max = 30): string {
  if (!value) return '—'
  const s = String(value)
  return s.length > max ? `${s.slice(0, max)}` : s
}

function padApplicantId(id: number | undefined): string {
  if (id == null) return ''
  return String(id).padStart(8, '0')
}

function formatCreatedAt(value: string | undefined): string {
  if (!value) return '—'
  const d = new Date(value)
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
  }
  return String(value)
}

export function ApiLogsPage() {
  const { roleName, propertyId, hasWriteAccess } = useUserRole()
  const isCompanyRole = (COMPANY_ROLES as readonly string[]).includes(roleName)
  const canOpenApplication = hasWriteAccess('application') && !isCompanyRole

  const [params, setParams] = useState<ApiLogsListParams>(initialParams)
  const [searchDraft, setSearchDraft] = useState(() => params.q)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [rows, setRows] = useState<ApiLogRow[]>([])
  const [pageOptions, setPageOptions] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (propertyId && !params.property_id) {
      setParams((p) => ({ ...p, property_id: String(propertyId) }))
    }
  }, [propertyId, params.property_id])

  useEffect(() => {
    setSearchDraft(params.q)
  }, [params.q])

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextQ = searchDraft.trim()
      if (nextQ !== params.q.trim()) {
        setParams((prev) => ({ ...prev, q: nextQ, page: 1 }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchDraft, params.q])

  const load = useCallback(async (next: ApiLogsListParams) => {
    setLoading(true)
    try {
      const res = await getApiLogList(buildApiLogsQueryParams(next))
      setRows(res.data ?? [])
      setPageOptions({
        current: res.current ?? 1,
        last: Math.max(1, res.last ?? 1),
        total: res.total ?? 0,
      })
      writeApiLogsSession(buildApiLogsSessionFromParams(next))
    } catch (err) {
      showApiError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(params)
  }, [load, params])

  const drawerFilters: ApiLogsFilterState = useMemo(
    () => ({ fromDt: params.fromDt, toDt: params.toDt }),
    [params.fromDt, params.toDt],
  )

  const activeFilterCount = useMemo(
    () => countActiveApiLogsFilters({ ...drawerFilters, q: params.q }),
    [drawerFilters, params.q],
  )

  const applyDrawerFilters = useCallback((next: ApiLogsFilterState) => {
    setParams((prev) => ({ ...prev, ...next, page: 1 }))
  }, [])

  const resetFilters = () => {
    setSearchDraft('')
    clearApiLogsSession()
    setParams({
      ...DEFAULT_PARAMS,
      property_id: propertyId ? String(propertyId) : '',
    })
  }

  const sort = (orderBy: ApiLogsSortKey) => {
    setParams((prev) => ({
      ...prev,
      sortBy:
        prev.orderBy === orderBy && prev.sortBy === 'desc' ? 'asc' : 'desc',
      orderBy,
      page: 1,
    }))
  }

  return (
    <div data-testid="api-logs-page">
      <PageShell
        title="API Logs"
        pageHeaderTestIdRoot="api-logs-page-header"
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        <ListSearchToolbar
          searchValue={searchDraft}
          searchPlaceholder="Search.."
          onSearchChange={setSearchDraft}
          onOpenFilters={() => setIsFilterOpen(true)}
          activeFilterCount={activeFilterCount}
          testIdPrefix="api-logs"
        />

        <ApiLogsFilters
          filters={drawerFilters}
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={applyDrawerFilters}
          onReset={resetFilters}
        />

        <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm">
          <ListPaginationBar
            placement="header"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="api-logs"
            summaryLayout="angular"
          />

          {loading ? (
            <div className="flex min-h-[12rem] items-center justify-center py-16">
              <Loader2
                className="h-8 w-8 animate-spin text-[rgb(21_151_255)]"
                aria-hidden
              />
            </div>
          ) : (
            <ReportTableScroll
              enabled={rows.length > 0}
              testIdPrefix="api-logs"
            >
              <table
                className={cn(
                  reportStyles.listingTable,
                  'border-collapse text-left text-slate-800',
                )}
              >
                <thead>
                  <tr>
                    {SORT_COLUMNS.map((col) => {
                      const active = params.orderBy === col.key
                      return (
                        <th key={col.key} className="text-left">
                          <button
                            type="button"
                            onClick={() => sort(col.key)}
                            className={cn(
                              'inline-flex items-center gap-1.5 font-semibold text-slate-600 hover:text-slate-900',
                              active && 'text-[rgb(21_151_255)]',
                            )}
                          >
                            <span>{col.label}</span>
                            <span className={cn('inline-flex items-center ml-1 text-[11px]', active ? 'text-[#1597ff]' : 'text-slate-400')}>
                              {active ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3 shrink-0" aria-hidden />}
                            </span>
                          </button>
                        </th>
                      )
                    })}
                    <th className="text-right font-semibold text-slate-600">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={SORT_COLUMNS.length + 1}
                        className="py-12 text-center text-slate-500"
                      >
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr
                        key={row.api_log_id ?? `row-${index}`}
                        className="border-b border-slate-100"
                      >
                        <td>{row.api_log_id ?? '—'}</td>
                        <td>
                          {row.fk_applicant_id != null ? (
                            canOpenApplication ? (
                              <Link
                                to={ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(
                                  row.fk_applicant_id,
                                )}
                                className="text-[#007BFF] hover:underline"
                              >
                                {padApplicantId(row.fk_applicant_id)}
                              </Link>
                            ) : (
                              <span className="text-slate-700">
                                {padApplicantId(row.fk_applicant_id)}
                              </span>
                            )
                          ) : (
                            '—'
                          )}
                        </td>
                        <td
                          className="max-w-[12rem] truncate"
                          title={row.request_body}
                        >
                          {truncateBody(row.request_body)}
                        </td>
                        <td
                          className="max-w-[12rem] truncate"
                          title={row.response_body}
                        >
                          {truncateBody(row.response_body)}
                        </td>
                        <td>{row.api_type ?? '—'}</td>
                        <td>{row.api_method ?? '—'}</td>
                        <td>{formatCreatedAt(row.created_at)}</td>
                        <td className="text-right">
                          {row.api_log_id != null ? (
                            <Link
                              to={`/reports/api-logs/${row.api_log_id}`}
                              className="inline-flex rounded-lg p-2 text-slate-600 hover:bg-slate-100"
                              title="View"
                            >
                              <Eye className="h-4 w-4" aria-hidden />
                            </Link>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ReportTableScroll>
          )}

          <ListPaginationBar
            placement="footer"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix="api-logs"
            summaryLayout="angular"
          />
        </div>
      </PageShell>
    </div>
  )
}
